/**
 * ChipMUI — единая пилюля на базе @mui/material Chip, закрывающая оба
 * сценария сразу:
 *
 *   `mode='chip'` (default) — интерактивный токен: фильтр, тег в инпуте,
 *     quick-action. Поддерживает hover, onClick, onRemove, selected.
 *   `mode='badge'` — информационная inline-пилюля без интерактивности;
 *     onClick / onRemove / selected игнорируются.
 *
 * Геометрия — ЕДИНАЯ и ТОКЕН-УПРАВЛЯЕМАЯ через `pillGeometry(size)`
 * (ui/theme/controlGeometry): height из control.height, padX/padXCompact/gap
 * из spacing, radius = height/2, размер иконок из labelRole через
 * iconPxForRole. Никаких GEO-таблиц с числами — обе роли на одной шкале.
 *
 * Цвета — ТОЛЬКО через CSS-переменные `--ads-palette-*`, а не через
 * `theme.palette.*`. MUI v6 не конвертит наши кастомные слоты палитры
 * (surface, good/bad/alert, *.container) в var() при чтении внутри
 * styled-каллбэка: литерал активной схемы запекается в CSS-правило, и
 * `<SurfaceContextProvider value='light'>` физически нечего переопределять.
 * Читать значения тут — значит сломать светлый контекст (Modal и др.).
 *
 * Семантика корневого элемента выбирается по слотам — от этого зависит
 * всё поведение с клавиатуры:
 *
 *   есть крестик              → корень `div` (ButtonBase), крестик внутри —
 *                               настоящая `<button>`. Вложенная `<button>`
 *                               в `<button>` невалидна, поэтому корень
 *                               кнопкой не делаем.
 *   задан `href`              → настоящая `<a href>`: хеш в адресе,
 *                               «назад», открытие в новой вкладке.
 *   кликабельный без крестика → настоящая `<button type='button'>`:
 *                               нативные Enter/Space, нативный disabled.
 *   остальное (badge, статика)→ `<span>`: не таб-стоп, не виджет, валиден
 *                               внутри `<p>`.
 *
 * @format
 */

import {
  forwardRef,
  isValidElement,
  type ElementType,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import {Box, Chip as MuiChip, type ChipProps as MuiChipProps} from '@mui/material';
import {styled, type CSSObject} from '@mui/material/styles';

import {text, iconPxForRole} from 'ui/theme/typography';
import {pillGeometry, type ControlSize} from 'ui/theme/controlGeometry';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {focusRing, disabledStyle} from 'ui/theme/state';
import opacityTokens from 'ui/tokens/semantic/opacity.json';
import {v} from 'ui/tokens/util';

import {Icon} from '../Icon';

export const CHIPMUI_MODES = ['chip', 'badge'] as const;
export const CHIPMUI_SIZES = ['tiny', 'xs', 's', 'sm', 'm'] as const;
export const CHIPMUI_COLORS = ['primary', 'good', 'bad', 'alert', 'neutral'] as const;
export const CHIPMUI_VARIANTS = ['solid', 'soft', 'outline'] as const;

export type ChipMUIMode = (typeof CHIPMUI_MODES)[number];
export type ChipMUISize = (typeof CHIPMUI_SIZES)[number];
export type ChipMUIColor = (typeof CHIPMUI_COLORS)[number];
export type ChipMUIVariant = (typeof CHIPMUI_VARIANTS)[number];

/** aria-label крестика удаления. Атомы DS не ходят в i18n — как и легаси Chip. */
const REMOVE_LABEL = 'Remove';

const OVERLAY_OPACITY = v(opacityTokens.opacity.muted);
const REMOVE_IDLE_OPACITY = v(opacityTokens.opacity.high);
const REMOVE_ACTIVE_OPACITY = v(opacityTokens.opacity.full);

// ── Props ──────────────────────────────────────────────────────────────
// Порядок групп — по конвенции DS: состояние → визуальные → контент →
// поведение → layout → escape hatches. Storybook рендерит Controls в
// порядке интерфейса, поэтому порядок user-facing.

export interface ChipMUIProps
  extends Omit<
    MuiChipProps,
    | 'color'
    | 'variant'
    | 'size'
    | 'label'
    | 'icon'
    | 'deleteIcon'
    | 'onDelete'
    | 'onClick'
    | 'children'
    | 'component'
  > {
  /** Selected-состояние (только `mode='chip'`): заливка role.main. */
  selected?: boolean;

  /**
   * Сценарий пилюли. `chip` — интерактивный токен (фильтр, тег в инпуте,
   * quick-action): hover, onClick, onRemove. `badge` — информационная
   * inline-пилюля без интерактивности; onClick/onRemove игнорируются.
   */
  mode?: ChipMUIMode;

  /** Semantic role — communicates intent, not visual hue. */
  color?: ChipMUIColor;

  /** Height по общей размерной шкале DS: tiny=18, xs=24, s=32, sm=36, m=48. */
  size?: ChipMUISize;

  /** Visual weight. `solid` filled, `soft` container-tinted, `outline` border-only. */
  variant?: ChipMUIVariant;

  /**
   * Left icon. Material Symbols ligature name (string) для типового
   * случая или React-элемент для прямого контроля осей/анимации.
   */
  leftIcon?: string | ReactNode;

  /** Label пилюли. */
  children?: ReactNode;

  /**
   * Клик по всей пилюле (только `mode='chip'`). Событие отдаётся наружу:
   * ссылочной пилюле нужен `preventDefault()`, когда переход
   * перехватывают — например ради плавного скролла к якорю.
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void;

  /**
   * Превращает пилюлю в ссылку: корень рендерится `<a href>`. Нужен там,
   * где клик это переход (якорь на блок, ссылка на раздел) — тогда
   * работают хеш в адресе, «назад», открытие в новой вкладке.
   *
   * Игнорируется вместе с крестиком удаления: ссылка с вложенной кнопкой
   * это вложенные интерактивные элементы, так что при `onRemove` корень
   * остаётся прежним.
   */
  href?: string;

  /**
   * Цель перехода. Осмысленна только вместе с `href` — на не-ссылочном
   * корне (`span`, `button`) атрибут не появится.
   *
   * Внутристраничному якорю нужен явный `_self` там, где документ несёт
   * `<base target>`: Storybook отдаёт превью с `<base target='_parent'>`,
   * и без этого якорь уводит из фрейма вместо прокрутки.
   */
  target?: string;

  /**
   * Отношение к целевому документу. При `target='_blank'` и незаданном
   * `rel` подставляется `noopener noreferrer` — так же, как это делает
   * `Link` со своим `external`. Оставлять новое окно с доступом к
   * `window.opener` небезопасно, и решать это на каждом вызове нельзя.
   */
  rel?: string;

  /**
   * Показ крестика удаления и его обработчик (только `mode='chip'`).
   * Крестик — отдельная таб-остановка с `aria-label`; в disabled он
   * остаётся на месте (не даёт пилюле прыгать по ширине), но выключен.
   */
  onRemove?: () => void;

  /** Выключенное состояние. */
  disabled?: boolean;

  /**
   * Растянуть на всю ширину родителя. Лейбл при нехватке места режется
   * многоточием. Нужен, когда пилюли встают равными ячейками в ряд.
   */
  fullWidth?: boolean;

  /**
   * Скругление. `true` (default) — полная пилюля (radius = height/2).
   * `false` — прямые углы: пилюля становится ячейкой составного
   * контрола, а внешние углы скругляет уже контейнер.
   */
  rounded?: boolean;

  /** Доступное имя — обязателен для пилюли без видимого текста. */
  ariaLabel?: string;

  /** Pass-through className для точечных переопределений. */
  className?: string;
}

// ── Палитра ────────────────────────────────────────────────────────────

type BrandColor = Exclude<ChipMUIColor, 'neutral'>;

/** Слот брендовой роли как CSS-var — см. шапку файла про MUI v6 и var(). */
const brandVar = (color: BrandColor, slot: string): string => `var(--ads-palette-${color}-${slot})`;

interface PillPalette {
  bg: string;
  bgHover: string;
  fg: string;
  border: string;
  /** Полупрозрачная заливка поверх фона (neutral soft у badge). */
  overlay: boolean;
}

function paletteFor(
  mode: ChipMUIMode,
  variant: ChipMUIVariant,
  color: ChipMUIColor,
  selected: boolean,
): PillPalette {
  const base = {overlay: false};

  if (mode === 'chip' && selected) {
    if (color === 'neutral') {
      return {
        ...base,
        bg: surfaceVar('surface3'),
        bgHover: surfaceVar('surface3'),
        fg: surfaceVar('onSurface'),
        border: 'transparent',
      };
    }
    return {
      ...base,
      bg: brandVar(color, 'main'),
      bgHover: brandVar(color, 'hover'),
      fg: brandVar(color, 'contrastText'),
      border: 'transparent',
    };
  }

  if (color === 'neutral') {
    if (mode === 'badge') {
      if (variant === 'solid') {
        return {
          ...base,
          bg: surfaceVar('inverseSurface'),
          bgHover: surfaceVar('inverseSurface'),
          fg: surfaceVar('inverseOnSurface'),
          border: 'transparent',
        };
      }
      if (variant === 'soft') {
        // Полупрозрачный оверлей: onSurface на opacity.muted. Красится
        // псевдоэлементом, потому что CSS-var нельзя «разбавить» в
        // rgba() без color-mix — а хардкодить rgba запрещено.
        return {
          bg: 'transparent',
          bgHover: 'transparent',
          fg: surfaceVar('onSurface'),
          border: 'transparent',
          overlay: true,
        };
      }
      return {
        ...base,
        bg: 'transparent',
        bgHover: 'transparent',
        fg: surfaceVar('onSurface'),
        border: surfaceVar('onSurfaceMuted'),
      };
    }
    if (variant === 'solid') {
      return {
        ...base,
        bg: surfaceVar('surface2'),
        bgHover: surfaceVar('surface3'),
        fg: surfaceVar('onSurface'),
        border: 'transparent',
      };
    }
    if (variant === 'soft') {
      return {
        ...base,
        bg: surfaceVar('surface1'),
        bgHover: surfaceVar('surface2'),
        fg: surfaceVar('onSurfaceLight'),
        border: 'transparent',
      };
    }
    return {
      ...base,
      bg: 'transparent',
      bgHover: surfaceVar('surface1'),
      fg: surfaceVar('onSurfaceLight'),
      border: surfaceVar('onSurfaceMuted'),
    };
  }

  if (variant === 'solid') {
    return {
      ...base,
      bg: brandVar(color, 'main'),
      bgHover: brandVar(color, 'hover'),
      fg: brandVar(color, 'contrastText'),
      border: 'transparent',
    };
  }
  if (variant === 'soft') {
    return {
      ...base,
      bg: brandVar(color, 'container'),
      bgHover: brandVar(color, 'hoverContainer'),
      fg: brandVar(color, 'onContainer'),
      border: 'transparent',
    };
  }
  return {
    ...base,
    bg: 'transparent',
    bgHover: brandVar(color, 'container'),
    fg: brandVar(color, 'main'),
    border: brandVar(color, 'main'),
  };
}

// ── Корень ─────────────────────────────────────────────────────────────

const Root = styled(MuiChip, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  /** Полиморфизм MUI теряется при styled(); объявляем пропы явно. */
  component?: ElementType;
  /** Ходит на корень только когда он `<a>` — см. выбор элемента ниже. */
  href?: string;
  /** Спутники href, там же и по тому же условию. */
  target?: string;
  rel?: string;
  $mode: ChipMUIMode;
  $size: ControlSize;
  $variant: ChipMUIVariant;
  $color: ChipMUIColor;
  $selected: boolean;
  $clickable: boolean;
  $hasRemove: boolean;
  $rounded: boolean;
  $fullWidth: boolean;
}>(
  ({
    theme,
    $mode,
    $size,
    $variant,
    $color,
    $selected,
    $clickable,
    $hasRemove,
    $rounded,
    $fullWidth,
  }) => {
    const g = pillGeometry($size);
    const p = paletteFor($mode, $variant, $color, $selected);
    const removePx = iconPxForRole(g.labelRole);
    // focusRing отдаёт `{'&:focus-visible': …}`; на ButtonBase-корне
    // фокус приезжает классом `.Mui-focusVisible`, поэтому вешаем на оба.
    const ring = focusRing(theme)['&:focus-visible'] as CSSObject;

    return {
      boxSizing: 'border-box',
      position: 'relative',
      height: g.height,
      maxWidth: '100%',
      // minWidth: 0 обязателен — иначе `min-width: auto` у flex-элемента
      // не даёт ячейке сжаться ниже собственного контента, и ряд
      // fullWidth-пилюль расходится по ширинам вместо равных долей.
      ...($fullWidth ? {width: '100%', minWidth: 0, flexShrink: 1} : {flexShrink: 0}),
      paddingLeft: g.padX,
      paddingRight: $hasRemove ? g.padXCompact : g.padX,
      borderRadius: $rounded ? g.height / 2 : 0,
      gap: g.gap,

      // Лейбл: MUI уже даёт ellipsis, нам остаётся снять его паддинг и
      // разрешить сжатие внутри flex-контейнера.
      '& .MuiChip-label': {padding: 0, minWidth: 0},
      '& .MuiChip-icon': {margin: 0, color: 'inherit', fontSize: 'inherit', flexShrink: 0},

      // Вес иконок отдельно не задаём: Icon без явного `weight` наследует
      // CSS font-weight, а его сюда приносит text(labelRole) — правило
      // «икона тянет вес из типо-роли» выполняется само.
      ...text(g.labelRole),
      lineHeight: 1,

      backgroundColor: p.bg,
      color: p.fg,
      border: `1px solid ${$variant === 'outline' ? p.border : 'transparent'}`,
      transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
        duration: theme.transitions.duration.shorter,
      }),
      cursor: $clickable ? 'pointer' : 'default',

      ...(p.overlay && {
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          backgroundColor: surfaceVar('onSurface'),
          opacity: OVERLAY_OPACITY,
          pointerEvents: 'none',
        },
        // ::before — позиционированный псевдоэлемент, он красится ПОСЛЕ
        // потоковых детей. Поднимаем контент в тот же слой, иначе текст
        // и иконки уедут под оверлей.
        '& > *': {position: 'relative'},
      }),

      '&:hover': {backgroundColor: $clickable ? p.bgHover : p.bg},
      '&:focus-visible': ring,
      '&.Mui-focusVisible': ring,
      '&.Mui-disabled': disabledStyle(),

      '& .MuiChip-deleteIcon': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: `calc(${removePx}px + ${theme.spacing(1)})`,
        height: `calc(${removePx}px + ${theme.spacing(1)})`,
        padding: 0,
        margin: 0,
        border: 0,
        background: 'transparent',
        borderRadius: '50%',
        color: 'inherit',
        cursor: 'pointer',
        opacity: REMOVE_IDLE_OPACITY,
        transition: theme.transitions.create(['opacity'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {opacity: REMOVE_ACTIVE_OPACITY, color: 'inherit'},
        '&:focus-visible': ring,
        '&:disabled': {cursor: 'not-allowed'},
      },
    };
  },
);

// MUI ждёт в слоте `icon` именно ReactElement (он его клонирует, чтобы
// подмешать класс), поэтому произвольный ReactNode оборачиваем.
function renderIcon(icon: string | ReactNode | undefined, px: number): ReactElement | undefined {
  if (!icon) return undefined;
  if (typeof icon === 'string') return <Icon name={icon} size={px} color='inherit' />;
  if (isValidElement(icon)) return icon;
  return <Box component='span'>{icon}</Box>;
}

export const ChipMUI = forwardRef<HTMLDivElement, ChipMUIProps>(function ChipMUI(
  {
    selected = false,
    mode = 'chip',
    color = 'primary',
    size = 's',
    variant = 'soft',
    leftIcon,
    children,
    onClick,
    href,
    target,
    rel,
    onRemove,
    disabled = false,
    fullWidth = false,
    rounded = true,
    ariaLabel,
    className,
    // Семантику корня атом выбирает сам, но молекула-обёртка вправе её
    // переопределить: чип внутри SegmentedControl — это radio/checkbox
    // своей группы, а не тогл-кнопка. Только эти два прохода наружу;
    // всё остальное из rest перебить нашу разметку не может.
    role: roleProp,
    'aria-checked': ariaCheckedProp,
    ...rest
  },
  ref,
) {
  const g = pillGeometry(size);
  const iconPx = iconPxForRole(g.labelRole);

  const isChip = mode === 'chip';
  const clickable = isChip && Boolean(onClick);
  const hasRemove = isChip && Boolean(onRemove);
  // Ссылкой пилюля становится только там, где это не создаёт вложенных
  // интерактивных элементов: крестик удаления внутри <a> недопустим.
  // Выключенная ссылка href не несёт — иначе по ней всё равно перейдут.
  const isLink = isChip && Boolean(href) && !hasRemove && !disabled;

  // Новое окно без rel оставляет целевой странице доступ к window.opener.
  // Дефолт тут, а не на вызове: забыть его — норма, а последствия
  // одинаковы во всех вызовах. Явный rel всегда сильнее.
  const linkRel = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  // См. шапку файла — выбор корня определяет всю семантику.
  // `undefined` = отдаём выбор MUI, а он под onDelete ставит ButtonBase/div.
  let rootComponent: ElementType | undefined;
  if (!hasRemove) {
    if (isChip && href) rootComponent = 'a';
    else rootComponent = clickable ? 'button' : 'span';
  }

  // onClick передаём и в disabled — иначе MUI не считает чип clickable и
  // не пробрасывает `disabled` на нативную кнопку. Блокировка — нативным
  // disabled плюс pointer-events из disabledStyle(); ранний выход тут на
  // случай не-кнопочного корня (div с крестиком).
  const handleClick = clickable
    ? (event: MouseEvent<HTMLElement>) => {
        if (!disabled) onClick?.(event);
      }
    : undefined;

  // Крестик рисуем и в disabled: иначе пилюля скачет по ширине при
  // переходе в выключенное состояние. Действие при этом заблокировано.
  const handleRemove = hasRemove
    ? () => {
        if (!disabled) onRemove?.();
      }
    : undefined;

  // Корень-виджет только там, где он действительно активируется. У
  // неинтерактивного корня с крестиком снимаем таб-остановку — единственный
  // фокусируемый элемент внутри это сам крестик.
  const isNativeButton = rootComponent === 'button';
  const rootRole = hasRemove && clickable ? 'button' : undefined;
  const rootTabIndex = hasRemove && !clickable ? -1 : undefined;

  return (
    <Root
      {...rest}
      ref={ref}
      component={rootComponent}
      className={className}
      label={children != null ? <Box component='span'>{children}</Box> : undefined}
      icon={renderIcon(leftIcon, iconPx)}
      onClick={handleClick}
      onDelete={handleRemove}
      deleteIcon={
        <Box component='button' type='button' aria-label={REMOVE_LABEL} disabled={disabled}>
          <Icon name='close' size={iconPx} color='inherit' />
        </Box>
      }
      href={isLink ? href : undefined}
      // Атрибуты ссылки живут и умирают вместе с href: на span/button они
      // бессмысленны, а через ...rest осели бы на любом корне.
      target={isLink ? target : undefined}
      rel={isLink ? linkRel : undefined}
      disabled={disabled}
      role={roleProp ?? rootRole}
      tabIndex={rootTabIndex}
      aria-label={ariaLabel}
      aria-checked={ariaCheckedProp}
      // aria-pressed и aria-checked взаимоисключающи: если обёртка взяла
      // на себя checked-семантику, тогл-состояние не дублируем. На ссылке
      // тогл-состояния нет вовсе — там текущность выражает aria-current.
      aria-pressed={
        ariaCheckedProp === undefined && clickable && !isLink ? selected : undefined
      }
      aria-disabled={(!isNativeButton && disabled) || undefined}
      $mode={mode}
      $size={size}
      $variant={variant}
      $color={color}
      $selected={selected}
      // Ссылка кликабельна и без onClick — курсор и hover ей нужны.
      $clickable={clickable || isLink}
      $hasRemove={hasRemove}
      $rounded={rounded}
      $fullWidth={fullWidth}
    />
  );
});
